import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  Autocomplete,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TablePagination,
  TextField,
  Tooltip,
} from "@mui/material";
import {
  ArrowUpward,
  ArrowBackIosNewRounded,
  Add,
  CameraAltOutlined,
  Close,
  Mic,
  Refresh,
  Search,
} from "@mui/icons-material";
import { useLocalStorage } from "@uidotdev/usehooks";
import { toast } from "react-toastify";
import { Link, useNavigate } from "react-router-dom";
import Sidebar from "../sidenav/Sidenav";
import isDesktop from "../isDesktop";
import { ReactComponent as Hemberger } from "../assets/icons/Hemberger.svg";
import { ReactComponent as UserIcon } from "../assets/icons/UserIcon.svg";
import styles from "./DoubtChat.module.css";

const THREAD_LIMIT_OPTIONS = [10, 20, 50];
const MESSAGE_LIMIT = 50;
const MAX_AUDIO_SIZE_BYTES = 25 * 1024 * 1024;
const MAX_IMAGE_SIZE_BYTES = 10 * 1024 * 1024;
const IMAGE_ACCEPT = ".jpg,.jpeg,.png,.webp,.gif,.heic,.heif,image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif";
const AUDIO_MIME_TYPE = "audio/webm";
const URL_PATTERN = /(?:https?:\/\/|www\.)[^\s<>"']+/gi;
const TRAILING_URL_PUNCTUATION = /[),.;:!?]+$/;

const STATUSES = [
  "open",
  "ai_answering",
  "waiting_user",
  "human_needed",
  "assigned",
  "resolved",
  "closed",
];

const SOURCES = ["pre_login", "mission_doubt", "general_doubt"];

const getApiBase = () =>
  `${process.env.REACT_APP_API_URL || ""}${
    process.env.REACT_APP_API_KEY || ""
  }/doubt-chat`;

const getAuthConfig = (token, options = {}) => ({
  ...options,
  headers: {
    ...(options.headers || {}),
    Authorization: token ? `Bearer ${token}` : undefined,
  },
});

const compact = (value) => (value == null ? "" : String(value).trim());

const titleize = (value) => compact(value).replace(/_/g, " ") || "-";

const getThreadId = (thread) =>
  thread?.id ?? thread?.threadId ?? thread?.thread_id ?? thread?._id;

const getMessageId = (message) =>
  message?.id ?? message?.messageId ?? message?.message_id ?? message?._id;

const getThreadLastMessage = (thread) =>
  thread?.lastMessage ?? thread?.last_message;

const getThreadLastMessageId = (thread) =>
  thread?.lastMessageId ??
  thread?.last_message_id ??
  thread?.lastMessageID;

const hasThreadLastMessageId = (thread) =>
  compact(getThreadLastMessageId(thread)) !== "";

const isDisplayableThread = (thread) =>
  hasThreadLastMessageId(thread) && getThreadLastMessage(thread) != null;

const getAttachmentId = (attachment) =>
  attachment?.attachmentId ??
  attachment?.attachment_id ??
  attachment?.id ??
  attachment?._id;

const toTimestamp = (value) => {
  if (!value) return 0;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
};

const formatDateAndTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const suffix = hours >= 12 ? "pm" : "am";
  hours = hours % 12 || 12;
  return `${day}.${month}.${year} ${hours}:${minutes}${suffix}`;
};

const formatDuration = (seconds) => {
  const totalSeconds = Math.max(0, Math.floor(Number(seconds) || 0));
  const minutes = Math.floor(totalSeconds / 60);
  const remainingSeconds = String(totalSeconds % 60).padStart(2, "0");
  return `${minutes}:${remainingSeconds}`;
};

const pickFirst = (...values) => values.find((value) => compact(value));

const normalizeAttachment = (attachment) => {
  if (!attachment) return null;
  const attachmentId = getAttachmentId(attachment);
  return {
    ...attachment,
    id: attachmentId,
    attachmentId,
    fileUrl:
      attachment.fileUrl ||
      attachment.file_url ||
      attachment.url ||
      attachment.location ||
      "",
  };
};

const getAttachmentPreview = (messageType) =>
  messageType === "image"
    ? "[Image]"
    : messageType === "audio"
    ? "[Audio]"
    : "[Attachment]";

const getMessagePreview = (message) => {
  const body = pickFirst(message?.body, message?.text, message?.message);
  const messageType = compact(
    message?.messageType || message?.message_type || message?.type
  ).toLowerCase();
  const attachments = Array.isArray(message?.attachments) ? message.attachments : [];

  if (body) return body;
  if (attachments.length || messageType === "image" || messageType === "audio") {
    return getAttachmentPreview(messageType);
  }
  return "";
};

const normalizeMessage = (message) => {
  const attachments = Array.isArray(message?.attachments)
    ? message.attachments.map(normalizeAttachment).filter(Boolean)
    : message?.attachment
    ? [normalizeAttachment(message.attachment)].filter(Boolean)
    : message?.fileUrl || message?.file_url
    ? [normalizeAttachment(message)].filter(Boolean)
    : [];
  const messageId = getMessageId(message);
  const body = pickFirst(message?.body, message?.text, message?.message);
  const createdAt = pickFirst(
    message?.createdAt,
    message?.created_at,
    message?.sentAt,
    message?.timestamp
  );
  const senderType = compact(
    message?.senderType || message?.sender_type || message?.sender || "system"
  ).toLowerCase();
  const messageType = compact(
    message?.messageType || message?.message_type || message?.type
  ).toLowerCase();

  return {
    ...message,
    id:
      messageId ||
      `${senderType}-${createdAt || Date.now()}-${body || "message"}`,
    messageId,
    threadId: message?.threadId || message?.thread_id || getThreadId(message?.thread),
    body,
    senderType,
    messageType,
    createdAt,
    attachments,
  };
};

const normalizeThread = (thread) => {
  const threadId = getThreadId(thread);
  const lastMessage = getThreadLastMessage(thread);
  const lastMessageId = getThreadLastMessageId(thread);
  const registeredUser =
    thread?.registeredUser ||
    thread?.registered_user ||
    thread?.user ||
    thread?.customer ||
    {};
  const lastMessagePreview = pickFirst(
    thread?.lastMessagePreview,
    thread?.last_message_preview,
    lastMessage?.body,
    lastMessage?.text,
    lastMessage?.message,
    thread?.preview
  );
  const lastMessageAt = pickFirst(
    thread?.lastMessageAt,
    thread?.last_message_at,
    lastMessage?.createdAt,
    lastMessage?.created_at,
    thread?.updatedAt,
    thread?.updated_at,
    thread?.createdAt,
    thread?.created_at
  );
  const userName = pickFirst(
    thread?.userName,
    thread?.user_name,
    thread?.name,
    registeredUser?.name,
    registeredUser?.fullName,
    registeredUser?.full_name,
    registeredUser?.username,
    registeredUser?.emp_name
  );
  const userEmail = pickFirst(
    thread?.userEmail,
    thread?.user_email,
    thread?.email,
    registeredUser?.email
  );
  const userPhone = pickFirst(
    thread?.userPhone,
    thread?.user_phone,
    thread?.phone,
    registeredUser?.phone,
    registeredUser?.mobile
  );
  const visitorSessionId = pickFirst(
    thread?.visitorSessionId,
    thread?.visitor_session_id,
    thread?.sessionId,
    thread?.session_id,
    thread?.visitorId,
    thread?.visitor_id
  );
  const missionName = pickFirst(
    thread?.missionName,
    thread?.mission_name,
    thread?.mission?.missionName,
    thread?.mission?.mission_name,
    thread?.mission?.name
  );
  const missionId = pickFirst(
    thread?.missionId,
    thread?.mission_id,
    thread?.mission?.missionId,
    thread?.mission?.mission_id,
    thread?.mission?.id
  );
  const missionLabel = missionName || (missionId ? `Mission ID: ${missionId}` : "");
  const visitorName = pickFirst(thread?.visitorName, thread?.visitor_name);
  const displayName =
    userName ||
    visitorName ||
    userEmail ||
    userPhone ||
    (visitorSessionId ? `Visitor ${String(visitorSessionId).slice(-8)}` : "Visitor");

  return {
    ...thread,
    id: threadId,
    threadId,
    lastMessage,
    lastMessageId,
    source: thread?.source || thread?.threadSource || thread?.thread_source || "",
    status: thread?.status || "open",
    userName,
    userEmail,
    userPhone,
    visitorSessionId,
    missionName,
    missionId,
    missionLabel,
    displayName,
    contactLine: [userEmail, userPhone].filter(Boolean).join(" | "),
    lastMessagePreview: lastMessagePreview || getMessagePreview(lastMessage),
    lastMessageAt,
    adminUnreadCount: Number(
      thread?.adminUnreadCount ??
        thread?.admin_unread_count ??
        thread?.unreadCount ??
        thread?.unread_count ??
        0
    ),
    assignedAdminId:
      thread?.assignedAdminId || thread?.assigned_admin_id || thread?.adminId || "",
  };
};

const extractItems = (response, keys) => {
  const root = response?.data;
  const payload = root?.data || root;
  if (Array.isArray(payload)) return payload;
  for (const key of keys) {
    if (Array.isArray(payload?.[key])) return payload[key];
  }
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const extractTotal = (response, fallback) => {
  const root = response?.data;
  const payload = root?.data || root;
  return Number(
    payload?.total ??
      payload?.totalCount ??
      payload?.total_count ??
      payload?.count ??
      payload?.pagination?.total ??
      fallback
  );
};

const getEventPayload = (event) => {
  if (!event?.data) return {};
  try {
    const parsed = JSON.parse(event.data);
    return parsed?.data || parsed;
  } catch {
    return {};
  }
};

const parseEventStreamBlock = (block) => {
  const event = { type: "message", data: "" };
  const data = [];

  block.split(/\r?\n/).forEach((line) => {
    if (!line || line.startsWith(":")) return;
    const separatorIndex = line.indexOf(":");
    const field = separatorIndex === -1 ? line : line.slice(0, separatorIndex);
    const value =
      separatorIndex === -1
        ? ""
        : line.slice(separatorIndex + 1).replace(/^ /, "");

    if (field === "event") {
      event.type = value || "message";
      return;
    }

    if (field === "data") {
      data.push(value);
    }
  });

  event.data = data.join("\n");
  return event;
};

const sortThreads = (threads) =>
  [...threads].sort((a, b) => toTimestamp(b.lastMessageAt) - toTimestamp(a.lastMessageAt));

const sortMessages = (messages) =>
  [...messages].sort((a, b) => toTimestamp(a.createdAt) - toTimestamp(b.createdAt));

const dedupeMessages = (messages) => {
  const map = new Map();
  messages.forEach((message) => {
    const normalized = normalizeMessage(message);
    map.set(normalized.id, normalized);
  });
  return sortMessages(Array.from(map.values()));
};

const isCustomerSender = (senderType) =>
  senderType === "visitor" || senderType === "user";

const getEmployeeId = (employee) =>
  employee?.emp_id || employee?.id || employee?._id || employee?.employeeId;

const getEmployeeName = (employee) =>
  employee?.emp_name || employee?.name || employee?.email || getEmployeeId(employee);

const getUserId = (user) =>
  user?.user_id || user?.userId || user?.id || user?._id || "";

const getUserLabel = (user) => {
  if (!user) return "";
  const username = compact(user.username);
  const name = compact(user.name || user.fullName || user.full_name);
  const email = compact(user.email);
  const phone = compact(user.phone || user.mobile);
  return [username || name || getUserId(user), email || phone]
    .filter(Boolean)
    .join(" | ");
};

const getLinkHref = (url) =>
  /^https?:\/\//i.test(url) ? url : `https://${url}`;

const splitTrailingUrlPunctuation = (url) => {
  const trailingMatch = url.match(TRAILING_URL_PUNCTUATION);
  if (!trailingMatch) return [url, ""];
  return [url.slice(0, -trailingMatch[0].length), trailingMatch[0]];
};

const renderLinkedText = (text) => {
  const value = String(text);
  const nodes = [];
  let lastIndex = 0;
  let match;

  URL_PATTERN.lastIndex = 0;
  while ((match = URL_PATTERN.exec(value)) !== null) {
    const [url, trailingPunctuation] = splitTrailingUrlPunctuation(match[0]);
    const offset = match.index;

    if (offset > lastIndex) {
      nodes.push(value.slice(lastIndex, offset));
    }

    nodes.push(
      <a
        className={styles.messageLink}
        href={getLinkHref(url)}
        key={`${url}-${offset}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        {url}
      </a>
    );

    if (trailingPunctuation) {
      nodes.push(trailingPunctuation);
    }

    lastIndex = offset + match[0].length;
  }

  if (lastIndex < value.length) {
    nodes.push(value.slice(lastIndex));
  }

  return nodes.length ? nodes : value;
};

const DoubtChat = () => {
  const navigate = useNavigate();
  const isDesktopDevice = isDesktop();
  const [token] = useLocalStorage("token", null);
  const apiBase = useMemo(getApiBase, []);

  const [threads, setThreads] = useState([]);
  const [threadsLoading, setThreadsLoading] = useState(false);
  const [threadsError, setThreadsError] = useState("");
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(20);
  const [totalThreads, setTotalThreads] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [assignedFilter, setAssignedFilter] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [employees, setEmployees] = useState([]);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [newChatOpen, setNewChatOpen] = useState(false);
  const [selectedNewUser, setSelectedNewUser] = useState(null);
  const [newChatSource, setNewChatSource] = useState("general_doubt");
  const [newChatMissionId, setNewChatMissionId] = useState("");
  const [newChatStageId, setNewChatStageId] = useState("");
  const [newChatSkillId, setNewChatSkillId] = useState("");
  const [newChatMessage, setNewChatMessage] = useState("");
  const [startingChat, setStartingChat] = useState(false);
  const [selectedThread, setSelectedThread] = useState(null);
  const selectedThreadRef = useRef(null);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesError, setMessagesError] = useState("");
  const [hasOlderMessages, setHasOlderMessages] = useState(false);
  const [olderMessagesLoading, setOlderMessagesLoading] = useState(false);
  const [replyBody, setReplyBody] = useState("");
  const [sendingText, setSendingText] = useState(false);
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [attachmentType, setAttachmentType] = useState("");
  const [recordingState, setRecordingState] = useState("idle");
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [recordedDurationSeconds, setRecordedDurationSeconds] = useState(0);
  const [recordedAudioBlob, setRecordedAudioBlob] = useState(null);
  const [recordedAudioUrl, setRecordedAudioUrl] = useState("");
  const [microphoneError, setMicrophoneError] = useState("");
  const [sendingAudio, setSendingAudio] = useState(false);
  const [sendingImage, setSendingImage] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [mobileViewport, setMobileViewport] = useState(null);
  const messagesEndRef = useRef(null);
  const imageInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const recordingStartedAtRef = useRef(0);

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  const closeMobileChat = () => {
    setSelectedThread(null);
    setMessages([]);
    setMessagesError("");
  };

  const stopRecordingTimer = useCallback(() => {
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
  }, []);

  const stopMediaStream = useCallback(() => {
    mediaStreamRef.current?.getTracks?.().forEach((track) => track.stop());
    mediaStreamRef.current = null;
  }, []);

  const clearRecordedAudio = useCallback(() => {
    setRecordedAudioBlob(null);
    setRecordedDurationSeconds(0);
    setRecordingSeconds(0);
    setRecordedAudioUrl((currentUrl) => {
      if (currentUrl) URL.revokeObjectURL(currentUrl);
      return "";
    });
  }, []);

  useEffect(() => {
    selectedThreadRef.current = selectedThread;
  }, [selectedThread]);

  useEffect(
    () => () => {
      stopRecordingTimer();
      stopMediaStream();
      setRecordedAudioUrl((currentUrl) => {
        if (currentUrl) URL.revokeObjectURL(currentUrl);
        return "";
      });
    },
    [stopMediaStream, stopRecordingTimer]
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(0);
      setDebouncedSearch(search.trim());
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    let animationFrameId = 0;

    const updateViewport = () => {
      if (animationFrameId) {
        window.cancelAnimationFrame(animationFrameId);
      }

      animationFrameId = window.requestAnimationFrame(() => {
        const viewport = window.visualViewport;
        setMobileViewport({
          height: viewport?.height || window.innerHeight,
          offsetTop: viewport?.offsetTop || 0,
        });
      });
    };

    updateViewport();

    const viewport = window.visualViewport;
    viewport?.addEventListener("resize", updateViewport);
    viewport?.addEventListener("scroll", updateViewport);
    window.addEventListener("resize", updateViewport);

    return () => {
      if (animationFrameId) {
        window.cancelAnimationFrame(animationFrameId);
      }
      viewport?.removeEventListener("resize", updateViewport);
      viewport?.removeEventListener("scroll", updateViewport);
      window.removeEventListener("resize", updateViewport);
    };
  }, []);

  const updateThreadListItem = useCallback((incomingThread) => {
    const normalizedThread = normalizeThread(incomingThread);
    if (!normalizedThread.id) return;
    setThreads((current) => {
      const index = current.findIndex((thread) => thread.id === normalizedThread.id);
      if (index === -1 && !isDisplayableThread(incomingThread)) return current;
      const next =
        index === -1
          ? [normalizedThread, ...current]
          : current.map((thread) =>
              thread.id === normalizedThread.id
                ? normalizeThread({ ...thread, ...incomingThread })
                : thread
            );
      return sortThreads(next);
    });
    setSelectedThread((current) =>
      current?.id === normalizedThread.id
        ? normalizeThread({ ...current, ...incomingThread })
        : current
    );
  }, []);

  const fetchThreads = useCallback(async () => {
    setThreadsLoading(true);
    setThreadsError("");
    try {
      const params = {
        page: page + 1,
        limit,
        status: statusFilter || undefined,
        source: sourceFilter || undefined,
        assignedAdminId: assignedFilter || undefined,
        search: debouncedSearch || undefined,
      };
      const response = await axios.get(
        `${apiBase}/admin/threads`,
        getAuthConfig(token, { params })
      );
      const items = extractItems(response, ["threads", "items", "rows"]);
      const normalizedThreads = items
        .filter(isDisplayableThread)
        .map(normalizeThread)
        .filter((thread) => thread.id);
      setThreads(sortThreads(normalizedThreads));
      setTotalThreads(
        Math.max(
          0,
          extractTotal(response, items.length) -
            (items.length - normalizedThreads.length)
        )
      );
    } catch (error) {
      console.error("Error fetching doubt chat threads:", error);
      setThreadsError(
        error.response?.data?.message || "Failed to fetch doubt chat threads."
      );
    } finally {
      setThreadsLoading(false);
    }
  }, [
    apiBase,
    assignedFilter,
    debouncedSearch,
    limit,
    page,
    sourceFilter,
    statusFilter,
    token,
  ]);

  const fetchEmployees = useCallback(async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL || ""}${
          process.env.REACT_APP_API_KEY || ""
        }/auth/employees/all`,
        getAuthConfig(token)
      );
      const items = extractItems(response, ["employees", "items"]);
      setEmployees(items);
    } catch (error) {
      console.error("Error fetching employees for doubt chat:", error);
    }
  }, [token]);

  const fetchUsers = useCallback(async () => {
    if (users.length > 0 || usersLoading) return;
    setUsersLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL || ""}${
          process.env.REACT_APP_API_KEY || ""
        }/user/all`,
        getAuthConfig(token)
      );
      const items = extractItems(response, ["users", "items"]);
      setUsers(items);
    } catch (error) {
      console.error("Error fetching users for doubt chat:", error);
      toast.error(error.response?.data?.message || "Failed to fetch users.");
    } finally {
      setUsersLoading(false);
    }
  }, [token, users.length, usersLoading]);

  useEffect(() => {
    fetchThreads();
  }, [fetchThreads]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  const markThreadRead = useCallback(
    async (threadId) => {
      if (!threadId) return;
      setThreads((current) =>
        current.map((thread) =>
          thread.id === threadId ? { ...thread, adminUnreadCount: 0 } : thread
        )
      );
      setSelectedThread((current) =>
        current?.id === threadId ? { ...current, adminUnreadCount: 0 } : current
      );
      try {
        await axios.post(
          `${apiBase}/threads/${encodeURIComponent(threadId)}/read`,
          {},
          getAuthConfig(token)
        );
      } catch (error) {
        console.error("Error marking doubt chat thread read:", error);
      }
    },
    [apiBase, token]
  );

  const fetchMessages = useCallback(
    async (threadId, beforeId) => {
      if (!threadId) return;
      const loadingSetter = beforeId ? setOlderMessagesLoading : setMessagesLoading;
      loadingSetter(true);
      setMessagesError("");
      try {
        const response = await axios.get(
          `${apiBase}/threads/${encodeURIComponent(threadId)}/messages`,
          getAuthConfig(token, {
            params: {
              limit: MESSAGE_LIMIT,
              beforeId: beforeId || undefined,
            },
          })
        );
        const items = extractItems(response, ["messages", "items", "rows"]);
        const normalizedMessages = items.map(normalizeMessage);
        if (beforeId) {
          setMessages((current) => dedupeMessages([...normalizedMessages, ...current]));
        } else {
          setMessages(dedupeMessages(normalizedMessages));
        }
        setHasOlderMessages(normalizedMessages.length === MESSAGE_LIMIT);
      } catch (error) {
        console.error("Error fetching doubt chat messages:", error);
        setMessagesError(
          error.response?.data?.message || "Failed to fetch doubt chat messages."
        );
      } finally {
        loadingSetter(false);
      }
    },
    [apiBase, token]
  );

  const openThread = useCallback(
    async (thread) => {
      const normalizedThread = normalizeThread(thread);
      setSelectedThread(normalizedThread);
      setMessages([]);
      await fetchMessages(normalizedThread.id);
      markThreadRead(normalizedThread.id);
    },
    [fetchMessages, markThreadRead]
  );

  useEffect(() => {
    if (!selectedThread?.id) return undefined;
    const onFocus = () => markThreadRead(selectedThread.id);
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [markThreadRead, selectedThread?.id]);

  useEffect(() => {
    if (!selectedThread?.id || olderMessagesLoading) return;
    messagesEndRef.current?.scrollIntoView({ block: "end" });
  }, [messages, olderMessagesLoading, selectedThread?.id]);

  const updateThreadFromMessage = useCallback(
    (message, incomingThread, fallbackThreadId) => {
      const normalizedMessage = normalizeMessage(message);
      const threadId =
        normalizedMessage.threadId ||
        fallbackThreadId ||
        getThreadId(incomingThread);
      if (!threadId) return;
      const currentThread = selectedThreadRef.current;
      const shouldClearUnread = currentThread?.id === threadId;
      const lastMessagePreview = getMessagePreview(normalizedMessage);
      const lastMessageId = normalizedMessage.messageId || normalizedMessage.id;

      if (incomingThread) {
        updateThreadListItem({
          ...incomingThread,
          id: threadId,
          lastMessage: normalizedMessage,
          lastMessageId,
          adminUnreadCount: shouldClearUnread
            ? 0
            : normalizeThread(incomingThread).adminUnreadCount,
        });
      } else {
        setThreads((current) => {
          const foundThread = current.some((thread) => thread.id === threadId);
          const nextThreads = current.map((thread) => {
            if (thread.id !== threadId) return thread;
            const customerUnread =
              isCustomerSender(normalizedMessage.senderType) && !shouldClearUnread
                ? thread.adminUnreadCount + 1
                : thread.adminUnreadCount;
            return {
              ...thread,
              lastMessage: normalizedMessage,
              lastMessageId,
              lastMessagePreview,
              lastMessageAt: normalizedMessage.createdAt || thread.lastMessageAt,
              adminUnreadCount: shouldClearUnread ? 0 : customerUnread,
            };
          });
          return sortThreads(
            foundThread
              ? nextThreads
              : [
                  normalizeThread({
                    id: threadId,
                    threadId,
                    status: "open",
                    lastMessage: normalizedMessage,
                    lastMessageId,
                    lastMessagePreview,
                    lastMessageAt:
                      normalizedMessage.createdAt || new Date().toISOString(),
                    adminUnreadCount:
                      isCustomerSender(normalizedMessage.senderType) &&
                      !shouldClearUnread
                        ? 1
                        : 0,
                  }),
                  ...nextThreads,
                ]
          );
        });
      }

      if (currentThread?.id === threadId) {
        setMessages((current) => dedupeMessages([...current, normalizedMessage]));
        markThreadRead(threadId);
      }
    },
    [markThreadRead, updateThreadListItem]
  );

  useEffect(() => {
    if (typeof fetch === "undefined" || typeof ReadableStream === "undefined") {
      return undefined;
    }

    let closed = false;
    const abortController = new AbortController();

    const handleThreadPayload = (payload) => {
      const thread = payload.thread || payload;
      if (!hasThreadLastMessageId(thread)) return;
      updateThreadListItem(thread);
    };

    const handleMessagePayload = (payload) => {
      updateThreadFromMessage(
        payload.message || payload,
        payload.thread || payload.threadData,
        payload.threadId || payload.thread_id
      );
    };

    const handleStreamEvent = (event) => {
      if (!event.data) return;
      const payload = getEventPayload(event);
      if (event.type === "thread_created" || event.type === "thread_updated") {
        handleThreadPayload(payload);
        return;
      }
      if (event.type === "message_created") {
        handleMessagePayload(payload);
      }
    };

    const connect = async () => {
      try {
        const response = await fetch(`${apiBase}/admin/stream`, {
          credentials: "include",
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          signal: abortController.signal,
        });

        if (!response.ok || !response.body) {
          throw new Error(`Admin stream failed with status ${response.status}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (!closed) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const blocks = buffer.split(/\r?\n\r?\n/);
          buffer = blocks.pop() || "";
          blocks.forEach((block) => handleStreamEvent(parseEventStreamBlock(block)));
        }
      } catch (error) {
        if (!closed && error.name !== "AbortError") {
          console.error("Doubt chat admin stream error:", error);
        }
      }
    };

    connect();

    return () => {
      closed = true;
      abortController.abort();
    };
  }, [apiBase, token, updateThreadFromMessage, updateThreadListItem]);

  const patchThread = useCallback(
    async (threadId, values, showSuccess = false) => {
      try {
        const response = await axios.patch(
          `${apiBase}/admin/threads/${encodeURIComponent(threadId)}`,
          values,
          getAuthConfig(token)
        );
        const updatedThread =
          response?.data?.data?.thread || response?.data?.thread || response?.data?.data || {
            id: threadId,
            ...values,
          };
        updateThreadListItem(updatedThread);
        if (showSuccess) toast.success("Thread updated successfully.");
      } catch (error) {
        console.error("Error updating doubt chat thread:", error);
        toast.error(error.response?.data?.message || "Failed to update thread.");
      }
    },
    [apiBase, token, updateThreadListItem]
  );

  const markWaitingUser = useCallback(
    async (threadId) => {
      setThreads((current) =>
        current.map((thread) =>
          thread.id === threadId ? { ...thread, status: "waiting_user" } : thread
        )
      );
      setSelectedThread((current) =>
        current?.id === threadId ? { ...current, status: "waiting_user" } : current
      );
      try {
        await axios.patch(
          `${apiBase}/admin/threads/${encodeURIComponent(threadId)}`,
          { status: "waiting_user" },
          getAuthConfig(token)
        );
      } catch (error) {
        console.error("Error setting doubt chat thread waiting_user:", error);
      }
    },
    [apiBase, token]
  );

  const appendSentMessage = useCallback(
    (threadId, rawMessage) => {
      const message = normalizeMessage(rawMessage);
      setMessages((current) => dedupeMessages([...current, message]));
      updateThreadFromMessage(message, {
        id: threadId,
        status: "waiting_user",
        lastMessage: message,
        lastMessageId: message.messageId || message.id,
        lastMessagePreview: getMessagePreview(message),
        lastMessageAt: message.createdAt || new Date().toISOString(),
        adminUnreadCount: 0,
      });
    },
    [updateThreadFromMessage]
  );

  const handleSendText = async () => {
    const body = replyBody.trim();
    if (!selectedThread?.id || !body || sendingText) return;
    setSendingText(true);
    try {
      const response = await axios.post(
        `${apiBase}/threads/${encodeURIComponent(selectedThread.id)}/messages`,
        { body },
        getAuthConfig(token)
      );
      const message =
        response?.data?.data?.message || response?.data?.message || response?.data?.data;
      appendSentMessage(selectedThread.id, message || {
        id: `admin-${Date.now()}`,
        body,
        senderType: "admin",
        createdAt: new Date().toISOString(),
        threadId: selectedThread.id,
      });
      setReplyBody("");
      markWaitingUser(selectedThread.id);
    } catch (error) {
      console.error("Error sending doubt chat reply:", error);
      toast.error(error.response?.data?.message || "Failed to send reply.");
    } finally {
      setSendingText(false);
    }
  };

  const resetAttachment = useCallback(() => {
    setAttachmentFile(null);
    setAttachmentType("");
    clearRecordedAudio();
    setMicrophoneError("");
    if (imageInputRef.current) imageInputRef.current.value = "";
  }, [clearRecordedAudio]);

  const handleImageFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      toast.error("Image file must be 10MB or smaller.");
      event.target.value = "";
      return;
    }
    clearRecordedAudio();
    setMicrophoneError("");
    setAttachmentFile(file);
    setAttachmentType("image");
    setRecordingState("idle");
  };

  const stopRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (recorder?.state === "recording") {
      recorder.stop();
    }
  }, []);

  const startRecording = useCallback(async () => {
    if (sendingText || sendingAudio || sendingImage) return;

    if (
      typeof navigator === "undefined" ||
      !navigator.mediaDevices?.getUserMedia ||
      typeof MediaRecorder === "undefined"
    ) {
      setMicrophoneError("Microphone recording is not supported in this browser.");
      return;
    }

    resetAttachment();
    setAttachmentType("audio");
    setMicrophoneError("");

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      mediaStreamRef.current = stream;
      mediaRecorderRef.current = recorder;
      audioChunksRef.current = [];
      recordingStartedAtRef.current = Date.now();

      recorder.ondataavailable = (event) => {
        if (event.data?.size) {
          audioChunksRef.current.push(event.data);
        }
      };

      recorder.onstop = () => {
        stopRecordingTimer();
        stopMediaStream();
        const durationSeconds = Math.max(
          1,
          Math.round((Date.now() - recordingStartedAtRef.current) / 1000)
        );
        const audioBlob = new Blob(audioChunksRef.current, {
          type: recorder.mimeType || AUDIO_MIME_TYPE,
        });

        if (!audioBlob.size) {
          setRecordingState("idle");
          setAttachmentType("");
          setMicrophoneError("Recording was empty. Please try again.");
          return;
        }

        if (audioBlob.size > MAX_AUDIO_SIZE_BYTES) {
          setRecordingState("idle");
          setAttachmentType("");
          setMicrophoneError("Audio recording must be 25MB or smaller.");
          return;
        }

        const previewUrl = URL.createObjectURL(audioBlob);
        setRecordedAudioBlob(audioBlob);
        setRecordedAudioUrl((currentUrl) => {
          if (currentUrl) URL.revokeObjectURL(currentUrl);
          return previewUrl;
        });
        setRecordedDurationSeconds(durationSeconds);
        setRecordingSeconds(durationSeconds);
        setAttachmentType("audio");
        setRecordingState("preview");
      };

      recorder.onerror = () => {
        stopRecordingTimer();
        stopMediaStream();
        setRecordingState("idle");
        setAttachmentType("");
        setMicrophoneError("Recording failed. Please try again.");
      };

      recorder.start();
      setRecordingState("recording");
      setRecordingSeconds(0);
      recordingTimerRef.current = setInterval(() => {
        setRecordingSeconds(
          Math.floor((Date.now() - recordingStartedAtRef.current) / 1000)
        );
      }, 500);
    } catch (error) {
      console.error("Error starting doubt chat audio recording:", error);
      stopRecordingTimer();
      stopMediaStream();
      setRecordingState("idle");
      setAttachmentType("");
      setMicrophoneError(
        error?.name === "NotAllowedError" || error?.name === "SecurityError"
          ? "Microphone permission denied."
          : "Could not access microphone."
      );
    }
  }, [
    resetAttachment,
    sendingAudio,
    sendingImage,
    sendingText,
    stopMediaStream,
    stopRecordingTimer,
  ]);

  const handleRecordingButtonClick = () => {
    if (recordingState === "recording") {
      stopRecording();
      return;
    }
    startRecording();
  };

  const cancelRecording = () => {
    if (recordingState === "recording") {
      mediaRecorderRef.current?.stop();
    }
    resetAttachment();
    setRecordingState("idle");
  };

  const handleSendAudio = async () => {
    if (
      !selectedThread?.id ||
      !recordedAudioBlob ||
      recordingState === "recording" ||
      sendingAudio ||
      sendingText ||
      sendingImage
    ) {
      return;
    }
    setSendingAudio(true);
    try {
      const caption = replyBody.trim();
      const audioFile = new File([recordedAudioBlob], "recording.webm", {
        type: recordedAudioBlob.type || AUDIO_MIME_TYPE,
      });
      const formData = new FormData();
      formData.append("audio", audioFile);
      if (caption) formData.append("caption", caption);
      if (recordedDurationSeconds) {
        formData.append("durationSeconds", String(recordedDurationSeconds));
      }
      const response = await axios.post(
        `${apiBase}/threads/${encodeURIComponent(selectedThread.id)}/audio`,
        formData,
        getAuthConfig(token, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })
      );
      const message =
        response?.data?.data?.message || response?.data?.message || response?.data?.data;
      const thread = response?.data?.data?.thread || response?.data?.thread;
      appendSentMessage(selectedThread.id, message || {
        id: `admin-audio-${Date.now()}`,
        body: caption,
        senderType: "admin",
        messageType: "audio",
        createdAt: new Date().toISOString(),
        threadId: selectedThread.id,
        attachments: [],
      });
      if (thread) updateThreadListItem(thread);
      resetAttachment();
      setReplyBody("");
      markWaitingUser(selectedThread.id);
    } catch (error) {
      console.error("Error sending doubt chat audio reply:", error);
      toast.error(error.response?.data?.message || "Failed to send audio reply.");
    } finally {
      setSendingAudio(false);
    }
  };

  const handleSendImage = async () => {
    if (
      !selectedThread?.id ||
      !attachmentFile ||
      recordingState === "recording" ||
      sendingImage ||
      sendingText ||
      sendingAudio
    ) {
      return;
    }
    setSendingImage(true);
    try {
      const caption = replyBody.trim();
      const formData = new FormData();
      formData.append("image", attachmentFile);
      if (caption) formData.append("caption", caption);
      const response = await axios.post(
        `${apiBase}/threads/${encodeURIComponent(selectedThread.id)}/image`,
        formData,
        getAuthConfig(token, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })
      );
      const message =
        response?.data?.data?.message || response?.data?.message || response?.data?.data;
      const thread = response?.data?.data?.thread || response?.data?.thread;
      appendSentMessage(selectedThread.id, message || {
        id: `admin-image-${Date.now()}`,
        body: caption,
        senderType: "admin",
        messageType: "image",
        createdAt: new Date().toISOString(),
        threadId: selectedThread.id,
        attachments: [],
      });
      if (thread) updateThreadListItem(thread);
      resetAttachment();
      setReplyBody("");
      markWaitingUser(selectedThread.id);
    } catch (error) {
      console.error("Error sending doubt chat image reply:", error);
      toast.error(error.response?.data?.message || "Failed to send image reply.");
    } finally {
      setSendingImage(false);
    }
  };

  const handleSendReply = () => {
    if (recordingState === "recording") return;
    if (recordedAudioBlob) {
      handleSendAudio();
      return;
    }
    if (attachmentType === "image") {
      handleSendImage();
      return;
    }
    handleSendText();
  };

  const handleStatusFilter = (event) => {
    setPage(0);
    setStatusFilter(event.target.value);
  };

  const handleSourceFilter = (event) => {
    setPage(0);
    setSourceFilter(event.target.value);
  };

  const handleAssignedFilter = (event) => {
    setPage(0);
    setAssignedFilter(event.target.value);
  };

  const handleStatusUpdate = (event) => {
    if (!selectedThread?.id) return;
    patchThread(selectedThread.id, { status: event.target.value }, true);
  };

  const handleAssignmentUpdate = (event) => {
    if (!selectedThread?.id) return;
    patchThread(
      selectedThread.id,
      { assignedAdminId: event.target.value || null },
      true
    );
  };

  const resetNewChatForm = () => {
    setSelectedNewUser(null);
    setNewChatSource("general_doubt");
    setNewChatMissionId("");
    setNewChatStageId("");
    setNewChatSkillId("");
    setNewChatMessage("");
  };

  const handleOpenNewChat = () => {
    setNewChatOpen(true);
    fetchUsers();
  };

  const handleCloseNewChat = () => {
    if (startingChat) return;
    setNewChatOpen(false);
    resetNewChatForm();
  };

  const handleStartNewChat = async () => {
    const userId = getUserId(selectedNewUser);
    const initialMessage = newChatMessage.trim();
    if (!userId) {
      toast.error("Please select a registered user.");
      return;
    }
    if (!initialMessage) {
      toast.error("Please enter an initial message.");
      return;
    }

    setStartingChat(true);
    try {
      const body = {
        userId,
        source: newChatSource,
        initialMessage,
      };
      if (newChatSource === "mission_doubt") {
        if (newChatMissionId.trim()) body.missionId = newChatMissionId.trim();
        if (newChatStageId.trim()) body.stageId = newChatStageId.trim();
        if (newChatSkillId.trim()) body.skillId = newChatSkillId.trim();
      }

      const response = await axios.post(
        `${apiBase}/threads`,
        body,
        getAuthConfig(token)
      );
      const payload = response?.data?.data || response?.data || {};
      const thread = payload.thread || payload;
      const message = payload.message;
      const normalizedThread = normalizeThread(thread);

      if (normalizedThread.id) {
        if (message) {
          const normalizedMessage = normalizeMessage({
            threadId: normalizedThread.id,
            ...message,
          });
          const threadWithLastMessage = normalizeThread({
            ...thread,
            id: normalizedThread.id,
            lastMessage: normalizedMessage,
            lastMessageId: normalizedMessage.messageId || normalizedMessage.id,
          });
          updateThreadListItem(threadWithLastMessage);
          setSelectedThread(threadWithLastMessage);
          setMessages(dedupeMessages([normalizedMessage]));
        } else {
          updateThreadListItem(normalizedThread);
          setSelectedThread(normalizedThread);
          fetchMessages(normalizedThread.id);
        }
        markThreadRead(normalizedThread.id);
      } else {
        fetchThreads();
      }

      toast.success("Doubt chat started successfully.");
      setNewChatOpen(false);
      resetNewChatForm();
    } catch (error) {
      console.error("Error starting doubt chat:", error);
      toast.error(error.response?.data?.message || "Failed to start doubt chat.");
    } finally {
      setStartingChat(false);
    }
  };

  const oldestMessageId = messages[0]?.id;
  const isRecording = recordingState === "recording";
  const isSendingReply = sendingText || sendingAudio || sendingImage;
  const canSendReply =
    Boolean(recordedAudioBlob || attachmentFile || replyBody.trim()) &&
    !isRecording &&
    !isSendingReply;
  const mobileViewportStyle = mobileViewport
    ? {
        "--doubt-chat-viewport-height": `${mobileViewport.height}px`,
        "--doubt-chat-viewport-offset-top": `${mobileViewport.offsetTop}px`,
      }
    : undefined;

  const renderMessage = (message) => {
    const isCustomer = isCustomerSender(message.senderType);
    const isSupport = message.senderType === "admin";
    const isAi = message.senderType === "ai";
    const rowClass = isCustomer
      ? styles.customerRow
      : isSupport
      ? styles.supportRow
      : isAi
      ? styles.aiRow
      : styles.systemRow;
    const bubbleClass = isCustomer
      ? styles.customerBubble
      : isSupport
      ? styles.supportBubble
      : isAi
      ? styles.aiBubble
      : styles.systemBubble;
    const firstAttachment = message.attachments?.[0];
    const attachmentUrl = firstAttachment?.fileUrl;
    const audioKey = firstAttachment
      ? getAttachmentId(firstAttachment) || attachmentUrl
      : undefined;
    const fileType = compact(firstAttachment?.fileType || firstAttachment?.file_type).toLowerCase();
    const mimeType = compact(firstAttachment?.mimeType || firstAttachment?.mime_type).toLowerCase();
    const isImageMessage =
      message.messageType === "image" ||
      fileType === "image" ||
      mimeType.startsWith("image/");
    const isAudioMessage =
      message.messageType === "audio" ||
      fileType === "audio" ||
      mimeType.startsWith("audio/");

    return (
      <div key={message.id} className={`${styles.messageRow} ${rowClass}`}>
        <div className={`${styles.bubble} ${bubbleClass}`}>
          <div className={styles.senderLabel}>
            {isCustomer
              ? "Customer"
              : isSupport
              ? "Support"
              : isAi
              ? "AI"
              : "System"}
          </div>
          {message.body && (
            <div className={styles.messageBody}>
              {renderLinkedText(message.body)}
            </div>
          )}
          {isImageMessage && attachmentUrl && (
            <img
              className={styles.messageImage}
              src={attachmentUrl}
              alt={firstAttachment?.originalFilename || "Chat attachment"}
            />
          )}
          {isAudioMessage && attachmentUrl && (
            <audio key={audioKey} className={styles.audio} controls src={attachmentUrl}>
              Your browser does not support audio playback.
            </audio>
          )}
          <div className={styles.messageTime}>{formatDateAndTime(message.createdAt)}</div>
        </div>
      </div>
    );
  };

  return (
    <div className={`container-fluid ${styles.page}`}>
      {!isDesktopDevice && (
        <>
          <div className={styles.mobileHeader}>
            <div
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className={styles.mobileMenuButton}
            >
              <Hemberger />
            </div>
            <Link to="/profile">
              <UserIcon />
            </Link>
          </div>
          <Sidebar isOpen={isSidebarOpen} onClose={closeSidebar} />
        </>
      )}
      <div className="align-center-all full-height">
        <Card elevation={3} className={`col-md-12 p-3 ${styles.card}`}>
          <CardContent className={styles.cardContent}>
            <div className={styles.headerRow}>
              <div className={styles.titleGroup}>
                <ArrowBackIosNewRounded
                  onClick={() => navigate("/dashboard")}
                  style={{ cursor: "pointer" }}
                />
                <h2
                  style={{ marginRight: "1rem", marginBottom: "0px" }}
                  className="page-header"
                >
                  Doubt Chat
                </h2>
              </div>
              <div className={styles.filters}>
                <TextField
                  className={styles.searchField}
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  variant="outlined"
                  placeholder="Search"
                  size="small"
                  InputProps={{
                    endAdornment: <Search />,
                  }}
                />
                <FormControl
                  className={`${styles.filterField} ${styles.statusFilterField}`}
                  size="small"
                >
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Status"
                    onChange={handleStatusFilter}
                  >
                    <MenuItem value="">All</MenuItem>
                    {STATUSES.map((status) => (
                      <MenuItem key={status} value={status}>
                        {titleize(status)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl
                  className={`${styles.filterField} ${styles.sourceFilterField}`}
                  size="small"
                >
                  <InputLabel>Source</InputLabel>
                  <Select
                    value={sourceFilter}
                    label="Source"
                    onChange={handleSourceFilter}
                  >
                    <MenuItem value="">All</MenuItem>
                    {SOURCES.map((source) => (
                      <MenuItem key={source} value={source}>
                        {titleize(source)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {employees.length > 0 && (
                  <FormControl
                    className={`${styles.filterField} ${styles.assignedFilterField}`}
                    size="small"
                  >
                    <InputLabel>Assigned</InputLabel>
                    <Select
                      value={assignedFilter}
                      label="Assigned"
                      onChange={handleAssignedFilter}
                    >
                      <MenuItem value="">All</MenuItem>
                      {employees.map((employee) => (
                        <MenuItem key={getEmployeeId(employee)} value={getEmployeeId(employee)}>
                          {getEmployeeName(employee)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                <Tooltip title="Refresh threads">
                  <IconButton
                    className={styles.toolbarIconButton}
                    onClick={fetchThreads}
                  >
                    <Refresh />
                  </IconButton>
                </Tooltip>
                <Tooltip title="New chat">
                  <IconButton
                    className={styles.newChatIconButton}
                    onClick={handleOpenNewChat}
                  >
                    <Add />
                  </IconButton>
                </Tooltip>
              </div>
            </div>

            <div className={styles.layout}>
              <div className={styles.threadPane}>
                {threadsLoading && threads.length === 0 ? (
                  <div className={styles.loadingState}>
                    <CircularProgress size={24} />
                  </div>
                ) : threadsError ? (
                  <div className={styles.errorState}>{threadsError}</div>
                ) : threads.length === 0 ? (
                  <div className={styles.emptyState}>No doubt chats found.</div>
                ) : (
                  <div className={styles.threadList}>
                    {threads.map((thread) => (
                      <button
                        key={thread.id}
                        type="button"
                        className={`${styles.threadItem} ${
                          selectedThread?.id === thread.id
                            ? styles.threadItemActive
                            : ""
                        }`}
                        onClick={() => openThread(thread)}
                      >
                        <div className={styles.threadTopLine}>
                          <div className={styles.threadIdentity}>
                            <div className={styles.threadName}>{thread.displayName}</div>
                            {thread.userPhone && (
                              <div className={styles.threadPhone}>
                                | {thread.userPhone}
                              </div>
                            )}
                          </div>
                          <div className={styles.threadTime}>
                            {formatDateAndTime(thread.lastMessageAt)}
                          </div>
                        </div>
                        {!thread.userPhone && thread.visitorSessionId && (
                          <div className={styles.threadContact}>
                            Session {thread.visitorSessionId}
                          </div>
                        )}
                        <div className={styles.threadPreview}>
                          {thread.lastMessagePreview}
                        </div>
                        <div className={styles.threadMetaLine}>
                          <span className={styles.badge}>{titleize(thread.source)}</span>
                          {thread.missionLabel && (
                            <span className={styles.badge}>{thread.missionLabel}</span>
                          )}
                          <span className={styles.badge}>{titleize(thread.status)}</span>
                          {thread.adminUnreadCount > 0 && (
                            <span className={styles.unreadBadge}>
                              {thread.adminUnreadCount}
                            </span>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                <TablePagination
                  className={styles.pagination}
                  component="div"
                  count={totalThreads}
                  page={page}
                  rowsPerPage={limit}
                  rowsPerPageOptions={THREAD_LIMIT_OPTIONS}
                  onPageChange={(event, nextPage) => setPage(nextPage)}
                  onRowsPerPageChange={(event) => {
                    setLimit(Number(event.target.value));
                    setPage(0);
                  }}
                />
              </div>

              <div
                className={`${styles.messagePane} ${
                  selectedThread ? styles.messagePaneOpen : ""
                }`}
                style={mobileViewportStyle}
              >
                {!selectedThread ? (
                  <div className={styles.emptyState}>
                    Select a thread to view messages.
                  </div>
                ) : (
                  <>
                    <div className={styles.chatHeader}>
                      <div className={styles.chatTopLine}>
                        <IconButton
                          className={styles.mobileChatBackButton}
                          onClick={closeMobileChat}
                        >
                          <ArrowBackIosNewRounded fontSize="small" />
                        </IconButton>
                        <div className={styles.chatTitleGroup}>
                          <div className={styles.chatTitle}>
                            {selectedThread.displayName}
                          </div>
                          <div className={styles.chatSubtitle}>
                            {[selectedThread.missionLabel]
                              .filter(Boolean)
                              .join(" | ")}
                          </div>
                        </div>
                        <div className={styles.statusControls}>
                          <FormControl className={styles.smallSelect} size="small">
                            <InputLabel>Status</InputLabel>
                            <Select
                              value={selectedThread.status || ""}
                              label="Status"
                              onChange={handleStatusUpdate}
                            >
                              {STATUSES.map((status) => (
                                <MenuItem key={status} value={status}>
                                  {titleize(status)}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          {employees.length > 0 && (
                            <FormControl className={styles.smallSelect} size="small">
                              <InputLabel>Assigned</InputLabel>
                              <Select
                                value={selectedThread.assignedAdminId || ""}
                                label="Assigned"
                                onChange={handleAssignmentUpdate}
                              >
                                <MenuItem value="">Unassigned</MenuItem>
                                {employees.map((employee) => (
                                  <MenuItem
                                    key={getEmployeeId(employee)}
                                    value={getEmployeeId(employee)}
                                  >
                                    {getEmployeeName(employee)}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className={styles.messages}>
                      {hasOlderMessages && (
                        <div className={styles.loadOlder}>
                          <Button
                            size="small"
                            onClick={() => fetchMessages(selectedThread.id, oldestMessageId)}
                            disabled={olderMessagesLoading}
                          >
                            {olderMessagesLoading ? "Loading..." : "Load older"}
                          </Button>
                        </div>
                      )}
                      {messagesLoading ? (
                        <div className={styles.loadingState}>
                          <CircularProgress size={24} />
                        </div>
                      ) : messagesError ? (
                        <div className={styles.errorState}>{messagesError}</div>
                      ) : messages.length === 0 ? (
                        <div className={styles.emptyState}>No messages yet.</div>
                      ) : (
                        messages.map(renderMessage)
                      )}
                      <div ref={messagesEndRef} />
                    </div>

                    <div className={styles.composer}>
                      <div className={styles.composerInputRow}>
                        <div className={styles.composerInputPill}>
                          <Tooltip title={attachmentType === "image" ? "Change image" : "Attach image"}>
                            <span>
                              <IconButton
                                className={`${styles.inlineAttachButton} ${
                                  attachmentType === "image" ? styles.inlineAttachButtonActive : ""
                                }`}
                                onClick={() => imageInputRef.current?.click()}
                                disabled={isSendingReply || isRecording}
                              >
                                <CameraAltOutlined />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <input
                            ref={imageInputRef}
                            type="file"
                            accept={IMAGE_ACCEPT}
                            hidden
                            onChange={handleImageFileChange}
                          />
                          <TextField
                            value={replyBody}
                            onChange={(event) => setReplyBody(event.target.value)}
                            placeholder={
                              attachmentFile || recordedAudioBlob
                                ? "Add a caption"
                                : "Type a reply"
                            }
                            size="small"
                            fullWidth
                            multiline
                            maxRows={4}
                            variant="standard"
                            className={styles.replyInput}
                            InputProps={{ disableUnderline: true }}
                            onKeyDown={(event) => {
                              if (event.key === "Enter" && !event.shiftKey) {
                                event.preventDefault();
                                handleSendReply();
                              }
                            }}
                          />
                          <Tooltip title={isRecording ? "Stop recording" : "Record audio"}>
                            <span>
                              <IconButton
                                className={`${styles.inlineAttachButton} ${
                                  attachmentType === "audio" ? styles.inlineAttachButtonActive : ""
                                } ${
                                  isRecording ? styles.recordingButton : ""
                                }`}
                                onClick={handleRecordingButtonClick}
                                disabled={isSendingReply}
                              >
                                <Mic />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Send reply">
                            <span>
                              <IconButton
                                className={styles.sendButton}
                                onClick={handleSendReply}
                                disabled={!canSendReply}
                              >
                                {isSendingReply ? (
                                  <CircularProgress size={20} color="inherit" />
                                ) : (
                                  <ArrowUpward />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        </div>
                      </div>
                      {microphoneError && (
                        <div className={styles.inlineError}>{microphoneError}</div>
                      )}
                      {isRecording && (
                        <div className={styles.recordingStatus}>
                          <span className={styles.recordingDot} />
                          Recording {formatDuration(recordingSeconds)}
                        </div>
                      )}
                      {recordedAudioUrl && recordingState === "preview" && (
                        <div className={styles.attachmentPreview}>
                          <audio controls src={recordedAudioUrl} className={styles.previewAudio}>
                            Your browser does not support audio playback.
                          </audio>
                          <span>{formatDuration(recordedDurationSeconds)}</span>
                          <Tooltip title="Delete recording">
                            <IconButton size="small" onClick={cancelRecording}>
                              <Close fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </div>
                      )}
                      {attachmentFile && (
                        <div className={styles.fileName}>
                          <span>
                            {titleize(attachmentType)}: {attachmentFile.name} (
                            {Math.ceil(attachmentFile.size / 1024)} KB)
                          </span>
                          <Tooltip title="Remove attachment">
                            <IconButton size="small" onClick={resetAttachment}>
                              <Close fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      <Dialog
        open={newChatOpen}
        onClose={handleCloseNewChat}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Start Doubt Chat</DialogTitle>
        <DialogContent>
          <Autocomplete
            options={users}
            loading={usersLoading}
            value={selectedNewUser}
            onChange={(event, value) => setSelectedNewUser(value)}
            getOptionLabel={getUserLabel}
            isOptionEqualToValue={(option, value) =>
              getUserId(option) === getUserId(value)
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Registered User"
                margin="normal"
                fullWidth
                required
              />
            )}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Source</InputLabel>
            <Select
              value={newChatSource}
              label="Source"
              onChange={(event) => setNewChatSource(event.target.value)}
            >
              <MenuItem value="general_doubt">General Doubt</MenuItem>
              <MenuItem value="mission_doubt">Mission Doubt</MenuItem>
            </Select>
          </FormControl>
          {newChatSource === "mission_doubt" && (
            <div className={styles.newChatOptionalGrid}>
              <TextField
                value={newChatSkillId}
                onChange={(event) => setNewChatSkillId(event.target.value)}
                label="Skill ID"
                margin="normal"
              />
              <TextField
                value={newChatStageId}
                onChange={(event) => setNewChatStageId(event.target.value)}
                label="Stage ID"
                margin="normal"
              />
              <TextField
                value={newChatMissionId}
                onChange={(event) => setNewChatMissionId(event.target.value)}
                label="Mission ID"
                margin="normal"
              />
            </div>
          )}
          <TextField
            value={newChatMessage}
            onChange={(event) => setNewChatMessage(event.target.value)}
            label="Initial Message"
            margin="normal"
            fullWidth
            multiline
            minRows={3}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseNewChat}
            disabled={startingChat}
            style={{
              textTransform: "none",
              border: "2.03px solid #37446F",
              color: "black",
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            className="filled-button"
            onClick={handleStartNewChat}
            disabled={!selectedNewUser || !newChatMessage.trim() || startingChat}
          >
            {startingChat ? "Starting" : "Start Chat"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default DoubtChat;
